// Delivery riders, phase 2 (F-DLV-N2): the phone-side buffer of GPS fixes
// waiting to be sent. Pure so the rules are testable without a browser.

import type { RiderLocationPoint } from '@/types/rider'

/** Matches the backend's per-request cap (riderPointsPerPost). */
export const MAX_BUFFERED_POINTS = 60
/** A new fix closer in time than this to the last buffered one is skipped. */
export const MIN_POINT_GAP_MS = 5_000
/** How often the buffer is sent. */
export const SEND_INTERVAL_MS = 15_000
/** With no new fix for this long, ask the phone for one (a parked bike sends nothing). */
export const HEARTBEAT_MS = 30_000

/** Adds a fix, skipping near-duplicates and keeping only the newest points. */
export function addPoint(buffer: RiderLocationPoint[], p: RiderLocationPoint): RiderLocationPoint[] {
  const last = buffer[buffer.length - 1]
  if (last && Math.abs(p.recorded_at - last.recorded_at) < MIN_POINT_GAP_MS) return buffer
  const next = [...buffer, p]
  return next.length > MAX_BUFFERED_POINTS ? next.slice(next.length - MAX_BUFFERED_POINTS) : next
}

export function pointFromPosition(pos: GeolocationPosition): RiderLocationPoint {
  const c = pos.coords
  return {
    lat: c.latitude,
    lng: c.longitude,
    accuracy_m: Number.isFinite(c.accuracy) ? Math.round(c.accuracy) : null,
    speed_mps: c.speed != null && Number.isFinite(c.speed) ? c.speed : null,
    heading: c.heading != null && Number.isFinite(c.heading) ? c.heading : null,
    recorded_at: pos.timestamp || Date.now(),
  }
}

/** "just now" / "40s ago" / "3m ago" / "2h ago". */
export function lastSeenLabel(atMs: number | null, nowMs: number): string {
  if (atMs == null) return 'never'
  const s = Math.max(0, Math.round((nowMs - atMs) / 1000))
  if (s < 10) return 'just now'
  if (s < 60) return `${s}s ago`
  const m = Math.round(s / 60)
  if (m < 60) return `${m}m ago`
  return `${Math.round(m / 60)}h ago`
}

/** Seen within 2 minutes = live; within 10 = stale; older = lost. */
export function locationFreshness(atMs: number | null, nowMs: number): 'live' | 'stale' | 'lost' {
  if (atMs == null) return 'lost'
  const age = nowMs - atMs
  return age <= 120_000 ? 'live' : age <= 600_000 ? 'stale' : 'lost'
}
